/// <reference types="vite/client" />
/**
 * Swappable auth client for Grymwyrd.
 *
 * Identity now federates to GameCodex Accounts (Atlas is the IdP — see
 * Atlas/_instructions/accounts-integration.md). The real provider is
 * `atlasAuthClient` (talks to our `/api/accounts/*` proxy). A fully-working
 * **local mock provider** remains for offline UI work and tests, behind the
 * same `AuthClient` interface so call sites never change.
 *
 * IMPORTANT: this is the *client surface* only. Authoritative authentication
 * and RBAC are enforced server-side on every protected API call. The roles /
 * tier carried in the session here are UI-gating hints, nothing more.
 *
 * Selection (see bottom of file):
 *   - `VITE_USE_MOCK_AUTH=1` or test mode → `mockAuthClient`.
 *   - Otherwise                           → `atlasAuthClient`.
 */
import { defaultAvatarFor } from '@/lib/avatar/startingAvatar'
import {
  useUserStore,
  type Tier,
  type UserProfile,
  type UserRole,
} from '@/stores/userStore'
import { atlasAuthClient } from './atlasAuthClient'

/** Local typing for env vars so we stay strict without touching tsconfig. */
interface AuthImportMetaEnv {
  readonly VITE_USE_MOCK_AUTH?: string
}
function authEnv(): AuthImportMetaEnv {
  return import.meta.env as unknown as AuthImportMetaEnv
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type OAuthProvider = 'google' | 'discord'

export interface SignUpInput {
  email: string
  password: string
  displayName: string
  /** Self-declared 13-or-older (COPPA gate — see RegisterPage). */
  isAdult: boolean
}

export interface Session {
  user: UserProfile
  token: string
}

/** Discriminated result so callers can branch without try/catch noise. */
export type AuthResult =
  | { ok: true; session: Session }
  | { ok: false; error: string }

export type SimpleResult = { ok: true } | { ok: false; error: string }

/**
 * The contract every provider implements. Keep it small and provider-agnostic;
 * Supabase, Clerk and the mock all map cleanly onto it.
 */
export interface AuthClient {
  signIn(email: string, password: string): Promise<AuthResult>
  signInWithProvider(provider: OAuthProvider): Promise<AuthResult>
  signUp(input: SignUpInput): Promise<AuthResult>
  signOut(): Promise<void>
  resetPassword(email: string): Promise<SimpleResult>
  /** Current persisted session (from the store), or null. */
  getSession(): Promise<Session | null>
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Well-known emails that grant elevated roles in the **mock provider only**,
 * so reviewers can exercise GM / admin UI without a backend. These mean
 * nothing in production — real RBAC is server-side.
 *
 *   admin@grymwyrd.com  → ['player','gm','moderator','admin']  (vault-premium)
 *   gm@grymwyrd.com     → ['player','gm']                       (vault-basic)
 *
 * Any other email → ['player'] / 'free'.
 */
const MOCK_ROLE_GRANTS: Record<string, { roles: UserRole[]; tier: Tier }> = {
  'admin@grymwyrd.com': {
    roles: ['player', 'gm', 'moderator', 'admin'],
    tier: 'vault-premium',
  },
  'gm@grymwyrd.com': {
    roles: ['player', 'gm'],
    tier: 'vault-basic',
  },
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/** Stable-ish id derived from the email so a given email keeps its identity. */
function mockUserId(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) | 0
  }
  return `mock_${(hash >>> 0).toString(36)}`
}

function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? 'Wanderer'
  return local.charAt(0).toUpperCase() + local.slice(1)
}

/** Deterministic default avatar: an in-world headshot where the world has them. */
function avatarFor(seed: string): string {
  return defaultAvatarFor(seed)
}

/**
 * A clearly-fake, unsigned JWT-shaped token so the rest of the app can treat a
 * mock session like a real one. NOT a security token — never trust it.
 */
function fakeJwt(user: Pick<UserProfile, 'id' | 'email' | 'roles'>): string {
  const enc = (obj: unknown): string =>
    btoa(JSON.stringify(obj)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')
  const header = enc({ alg: 'none', typ: 'JWT' })
  const now = Math.floor(Date.now() / 1000)
  const payload = enc({
    sub: user.id,
    email: user.email,
    roles: user.roles,
    iat: now,
    exp: now + 60 * 60, // 1h — purely cosmetic in mock mode
    iss: 'grymwyrd-mock',
  })
  return `${header}.${payload}.mock`
}

function buildMockProfile(
  email: string,
  opts: { displayName?: string } = {},
): UserProfile {
  const normalized = normalizeEmail(email)
  const grant = MOCK_ROLE_GRANTS[normalized] ?? { roles: ['player'], tier: 'free' }
  const displayName = opts.displayName?.trim() || displayNameFromEmail(normalized)
  return {
    id: mockUserId(normalized),
    email: normalized,
    displayName,
    avatarUrl: avatarFor(displayName || normalized),
    backstory: null,
    isPublic: true,
    roles: grant.roles,
    tier: grant.tier,
    createdAt: new Date().toISOString(),
  }
}

function commitSession(user: UserProfile): Session {
  const token = fakeJwt(user)
  useUserStore.getState().setSession(user, token)
  return { user, token }
}

/** Simulate provider latency so loading states are exercised in dev. */
function delay(ms = 450): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ---------------------------------------------------------------------------
// Mock provider (default in dev)
// ---------------------------------------------------------------------------

export const mockAuthClient: AuthClient = {
  async signIn(email, password) {
    await delay()
    if (!email || !password) {
      return { ok: false, error: 'Speak both your name and the secret word.' }
    }
    const user = buildMockProfile(email)
    return { ok: true, session: commitSession(user) }
  },

  async signInWithProvider(provider) {
    await delay()
    // In a real provider this redirects to the OAuth consent screen. The mock
    // fabricates a believable account for the chosen provider.
    const email = `${provider}-traveller@grymwyrd.com`
    const user = buildMockProfile(email, {
      displayName: provider === 'google' ? 'Google Wanderer' : 'Discord Wanderer',
    })
    return { ok: true, session: commitSession(user) }
  },

  async signUp(input) {
    await delay()
    if (!input.isAdult) {
      // COPPA: we do not create accounts for self-declared under-13 users.
      return {
        ok: false,
        error: 'You must be 13 or older to forge a pact with Grymwyrd.',
      }
    }
    if (!input.email || !input.password || !input.displayName) {
      return { ok: false, error: 'A name, an email and a secret word are all required.' }
    }
    const user = buildMockProfile(input.email, { displayName: input.displayName })
    return { ok: true, session: commitSession(user) }
  },

  async signOut() {
    await delay(150)
    // The hook also clears the store; clearing here keeps the client honest if
    // called directly.
    useUserStore.getState().clear()
  },

  async resetPassword(email) {
    await delay()
    if (!email) return { ok: false, error: 'Whisper the email tied to your soul.' }
    // No-op in mock mode — a real provider sends an email here.
    return { ok: true }
  },

  async getSession() {
    const { user, token } = useUserStore.getState()
    return user && token ? { user, token } : null
  },
}

// ---------------------------------------------------------------------------
// Active client
// ---------------------------------------------------------------------------

/**
 * Use the local mock provider only when explicitly opted in
 * (`VITE_USE_MOCK_AUTH=1`, for offline UI work) or under test; otherwise talk to
 * the real Atlas-backed GameCodex Accounts via the same-origin proxy. Every
 * page/hook imports `auth`, never a concrete provider — this is the only switch.
 */
export const usingMockAuth =
  authEnv().VITE_USE_MOCK_AUTH === '1' || import.meta.env.MODE === 'test'

export const auth: AuthClient = usingMockAuth ? mockAuthClient : atlasAuthClient

// Single import surface: re-export the Atlas magic-link + bootstrap helpers so
// callers reach everything via `@/lib/auth/authClient`.
export {
  requestMagicLink,
  verifyMagicLink,
  adoptSession,
  completeSessionFromAccessToken,
  setAccountPassword,
  bootstrapSession,
} from './atlasAuthClient'
